// NFTShowcase.tsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import NFTCard from './NFTCard';
import { Loader2, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';

const NFTShowcase = ({ maxItems = 6, compact = false }) => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const CONTRACT_ADDRESS = '0xa1d1E588412b998618C1bBc2029eAf5eFF30DE8b';
  const API_URL = `https://explorer.apertum.io/api/v2/tokens/${CONTRACT_ADDRESS}/instances`;

  const fetchNFTs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_URL, {
        headers: {
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.items && Array.isArray(data.items)) {
        // Take only the specified number of items and shuffle them
        const shuffled = [...data.items].sort(() => 0.5 - Math.random());
        setNfts(shuffled.slice(0, maxItems));
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (err) {
      console.error('Error fetching NFTs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNFTs();
  }, [maxItems]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="animate-spin w-8 h-8 text-blue-600 mb-4" />
        <p className="text-gray-600">Loading SportsFuture NFTs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Unable to Load NFTs
        </h3>
        <p className="text-gray-600 text-center mb-4 max-w-md">
          We're having trouble connecting to the Apertum Explorer. Please try again later.
        </p>
        <motion.button
          onClick={fetchNFTs}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <RefreshCw size={16} />
          <span>Try Again</span>
        </motion.button>
      </div>
    );
  }

  if (!nfts.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
          <span className="text-white font-bold text-xl">SF</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No NFTs Found
        </h3>
        <p className="text-gray-600 text-center">
          No SportsFuture NFTs are currently available to display.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header — omitted when a parent (home tabs) already titled the view */}
      {!compact && <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-4"
        >
          <span>Powered by Apertum Blockchain</span>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-white max-w-2xl mx-auto text-sm"
        >
          Discover unique digital collectibles from the SportsFuture Elite Series. 
          Each NFT represents athletic excellence and supports real athletes through our ecosystem.
        </motion.p>
      </div>}

      {/* NFT Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {nfts.map((nft, index) => (
          <NFTCard
            key={`${nft.id}-${index}`}
            nft={nft}
            delay={index * 0.1}
          />
        ))}
      </div>

      {/* View All Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-center"
      >
        <motion.a
          href={`https://openplaza.io`}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <span>Shop SportsFuture NFTs</span>
          <ExternalLink size={16} />
        </motion.a>
      </motion.div>
    </div>
  );
};

export default NFTShowcase;
